import type {
    ConfigTemplateFieldSchema,
    ConfigTemplateFieldSchemaResponse,
} from '../../../types/configTemplate.types';

const HINT_BY_PATH: Record<string, string> = {
    'storeConfig.salesConfig.excessDiscountOTP.enabled':
        'The requirement only. Named approvers are a people setting and never travel in a template.',
    'storeConfig.wholesaleConfig.enabled':
        'The store flag only. The wholesale invoice engine is a brand-level template.',
    'storeConfig.salesConfig.saleCompletionWhatsApp.enabled':
        'On or off only. The number the message goes to never travels in a template.',
    'storeConfig.addPurchaseConfig.rajasthanConfig.enabled':
        'The Rajasthan-only tax columns above only matter when this is on.',
    'storeConfig.stockVerificationConfig.showSystemStock':
        'Verification has its own show-system-stock. Stock audit has a separate one.',
    'storeConfig.stockAuditConfig.showSystemStock':
        'This is the audit setting. Verification has its own show-system-stock.',
    'storeConfig.counterConfig.autoShopCloseForSingleCounter':
        'Does nothing at a store until counters exist there. Counter go-live stays a separate flow.',
    'storeConfig.counterConfig.allowedRolesForEditingEntries':
        'Roles, not named people. Named authorisation never travels in a template.',
    'storeConfig.counterConfig.restrictRolesFromSeeingSalesDuringShopClose':
        'Separate from seeing sales while closing a counter. This one is about closing the shop.',
    'storeConfig.counterConfig.restrictedRolesForSeeingSalesDuringShopClose':
        'A block-list. Roles ticked here cannot see sales at shop close.',
    'storeConfig.whatsappNotifications.enabled':
        'On or off only. The number a sale-completion message goes to never travels in a template.',
    'storeConfig.storeHoursConfig.openingTime':
        'A single 24-hour clock time. Per-weekday timings are a separate field on purpose — both may be in one template.',
    'storeConfig.operationsConfig.timings':
        'Day 1 to day 7 (Monday to Sunday), each with its own opening and closing time. Kept apart from opening and closing time on purpose. The days in the template replace the whole timings list on apply.',
    'storeBrandSettings.wholesaleSettings.customOrderPrefix.enabled':
        'On or off only. There is no prefix string to configure.',
    'storeBrandSettings.wholesaleSettings.customerPricing.enabled': 'On or off only.',
    'storeBrandConfig.multiStoreAgentConfig.checkDeliveryRequest':
        'The flag only. Who receives the notification is a people setting and never travels in a template.',
    'storeBrandConfig.multiStoreAgentConfig.checkBulkOrderRequest':
        'The flag only. Who receives the notification is a people setting and never travels in a template.',
    'storeBrandSettings.customerSettings.businessTypes':
        'Applying replaces the store brand’s list with this one.',
    'storeBrandSettings.bulkSaleSettings.subcategoryOrder':
        'The order the template writes. Applying replaces the whole list.',
    'storeBrandSettings.expenseSettings.customExpenseCategories':
        'Each row is a category and its subcategories. Who created a row never travels in a template.',
    'storeBrandSettings.salesSettings.orderTypes':
        'A repeatable row editor — each row is one order type with a single name field.',
    'storeBrandSettings.dailyNotificationSettings.enabled':
        'On or off only. Which reports are on the daily WhatsApp list is owned by Reports admin and is not part of any template.',
};

const UNIT_BY_PATH: Record<string, string> = {
    'storeConfig.salesConfig.billLevelConfig.customerPhoneMandatoryAboveAmount.minimumAmount':
        '₹',
    'storeConfig.salesConfig.autoBilling.minimumBottles': 'bottles',
    'storeConfig.salesConfig.autoBilling.minimumItems': 'items',
    'storeConfig.salesConfig.cancelSale.windowDays': 'days',
    'storeConfig.salesConfig.backdatedSale.windowDays': 'days',
    'storeConfig.salesConfig.billSplitConfig.maxVolumePerBillMl': 'ml',
    'storeConfig.addPurchaseConfig.cancelPurchase.windowDays': 'days',
    'storeConfig.stockVerificationConfig.backdatedVerification.windowDays': 'days',
    'storeConfig.transferConfig.backdatedTransfer.windowDays': 'days',
    'storeConfig.counterConfig.businessDayOffsetMinutes': 'minutes',
};

const LIST_PLACEHOLDER_BY_PATH: Record<string, string> = {
    'storeBrandSettings.customerSettings.businessTypes': 'Add a business type',
    'storeBrandSettings.customerSettings.addressTypes': 'Add an address type',
    'storeBrandSettings.customerSettings.documentNames': 'Add a document name',
    'storeBrandSettings.customerSettings.customerTags': 'Add a customer tag',
    'storeBrandSettings.customerSettings.preferredCommunicationMethods': 'Add a method',
    'storeBrandSettings.creditNoteSettings.creditNoteTypes': 'Add a credit-note type',
    'storeBrandSettings.expenseSettings.paidTo': 'Add a payee label',
    'storeBrandSettings.bulkSaleSettings.subcategoryOrder': 'Add a subcategory',
    'storeBrandSettings.salesSettings.orderTypes': 'Add an order type',
};

const TEXT_PLACEHOLDER_BY_PATH: Record<string, string> = {
    'storeBrandConfig.wholesaleConfig.invoiceSettings.documentTitle':
        'Printed at the top of the invoice',
    'storeBrandConfig.wholesaleConfig.invoiceSettings.narrationBlock':
        'Standing narration line',
    'storeBrandConfig.wholesaleConfig.invoiceSettings.tncBlock':
        'Terms printed under the total',
    'storeBrandConfig.wholesaleConfig.invoiceSettings.eAndOEBlock':
        'Errors and omissions excepted.',
    'storeConfig.salesConfig.narration.name': 'Narration',
};

const TABLE_PREFIX_BY_PART_ID: Record<string, string> = {
    'items-table': 'Items table',
    'tax-table': 'Tax table',
    'rajasthan-columns': 'Rajasthan',
};

const LABEL_UNIT = /\((days|ml|minutes|bottles|items)\)$/i;

export function clusterCardTitle(
    partId: string | undefined,
    clusterTitle?: string | null
): string | undefined {
    if (!clusterTitle) return undefined;
    const prefix = partId ? TABLE_PREFIX_BY_PART_ID[partId] : undefined;
    if (!prefix) return clusterTitle;
    if (clusterTitle.startsWith(`${prefix} · `)) return clusterTitle;
    return `${prefix} · ${clusterTitle}`;
}

export function fieldHint(path: string): string | undefined {
    return HINT_BY_PATH[path];
}

export function fieldUnit(field: ConfigTemplateFieldSchema): string | undefined {
    if (UNIT_BY_PATH[field.path]) return UNIT_BY_PATH[field.path];
    const match = LABEL_UNIT.exec(field.label);
    return match?.[1]?.toLowerCase();
}

export function fieldListPlaceholder(field: ConfigTemplateFieldSchema): string {
    if (LIST_PLACEHOLDER_BY_PATH[field.path]) return LIST_PLACEHOLDER_BY_PATH[field.path];
    const label = field.label.trim().toLowerCase();
    if (!label) return 'Add';
    if (label.startsWith('order type')) return 'Add an order type';
    if (label.endsWith('s')) return `Add a ${label.slice(0, -1)}`;
    return `Add a ${label}`;
}

export function fieldTextPlaceholder(
    field: ConfigTemplateFieldSchema,
    clusterTitle?: string | null
): string | undefined {
    if (TEXT_PLACEHOLDER_BY_PATH[field.path]) return TEXT_PLACEHOLDER_BY_PATH[field.path];
    if (field.label === 'Label' && clusterTitle) return clusterTitle;
    return undefined;
}

const TIME_PLACEHOLDER_BY_PATH: Record<string, string> = {
    'storeConfig.storeHoursConfig.openingTime': '10:00',
    'storeConfig.storeHoursConfig.closingTime': '22:00',
};

export function fieldTimePlaceholder(field: ConfigTemplateFieldSchema): string {
    return TIME_PLACEHOLDER_BY_PATH[field.path] ?? '10:00';
}

export function applyPreviewLabels(
    schema: ConfigTemplateFieldSchemaResponse | null | undefined,
    included: string[]
): string[] {
    if (!schema) return [];
    const inSet = new Set(included);
    const labels: string[] = [];
    for (const group of schema.groups) {
        for (const part of group.parts) {
            for (const cluster of part.clusters) {
                for (const field of cluster.fields) {
                    if (!inSet.has(field.path)) continue;
                    const title = cluster.title?.trim();
                    if (title && field.label && field.label !== title) {
                        labels.push(`${title} · ${field.label}`);
                    } else {
                        labels.push(field.label || title || field.path);
                    }
                }
            }
        }
    }
    return labels;
}
